import { useState, useRef, useEffect } from 'react';
import { ResultModal } from '../../components/ResultModal';
import Axios from '../../Axios';
import './MobileJackpotSpin.css';

const RAD = Math.PI / 180;

// Alternating jackpot wheel colors
const JP_COLOR_DARK = '#7b120a';
const JP_COLOR_GOLD = '#f6d56c';
function jpAltColor(i) { return i % 2 === 0 ? JP_COLOR_DARK : JP_COLOR_GOLD; }
const JP_TEXT_LIGHT = '#fef08a';
const JP_TEXT_DARK = '#450a0a';
function jpAltTextColor(i) { return i % 2 === 0 ? JP_TEXT_LIGHT : JP_TEXT_DARK; }

const MOBILE_JACKPOT_SEGS = [
  { label: 'Jackpot Ticket\nx2', pct: 0, color: '#881337', textColor: '#fef08a', isTicket: true, stars: true },
  { label: '3%', pct: 3, color: '#fde047', textColor: '#450a0a' },
  { label: '5%', pct: 5, color: '#7f1d1d', textColor: '#fef08a' },
  { label: '10%', pct: 10, color: '#991b1b', textColor: '#fef08a' },
  { label: '20%', pct: 20, color: '#facc15', textColor: '#450a0a' },
  { label: '50%', pct: 50, color: '#7f1d1d', textColor: '#fef08a', stars: true },
  { label: 'Nothing', pct: 0, color: '#fde047', textColor: '#78350f' },
  { label: '100%', pct: 100, color: '#facc15', textColor: '#450a0a' },
  { label: '5%', pct: 5, color: '#7f1d1d', textColor: '#fef08a' },
];

export default function MobileJackpotSpin({ wallet, jackpotPool = 60025600, jackpotTickets = 0, segments: segmentsProp, onSpinResult }) {
  const segments = segmentsProp || MOBILE_JACKPOT_SEGS;
  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  
  const tickets = Number(jackpotTickets) || 0;
  const [spinning, setSpinning] = useState(false);
  const [winModal, setWinModal] = useState(null);

  const S = useRef({
    rotation: 0,
    isSpinning: false,
    spinStartTime: 0,
    startRotation: 0,
    totalRotation: 0,
    spinDuration: 5500,
    lastSeg: 0,
    winSeg: null,
  });

  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) audioCtxRef.current = new AudioContextClass();
    }
    const ac = audioCtxRef.current;
    if (ac && ac.state === 'suspended') ac.resume();
    return ac;
  };

  const playTick = (velocity) => {
    try {
      const ac = getAudioCtx();
      if (!ac) return;
      const t = ac.currentTime;
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(1200 + velocity * 100, t);
      osc.frequency.exponentialRampToValueAtTime(300, t + 0.04);
      gain.gain.setValueAtTime(Math.min(0.5, 0.15 + velocity * 0.015), t);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
      osc.connect(gain);
      gain.connect(ac.destination);
      osc.start(t);
      osc.stop(t + 0.05);
    } catch (_) {}
  };

  const playWin = () => {
    try {
      const ac = getAudioCtx();
      if (!ac) return;
      const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51, 1567.98]; // C5, E5, G5, C6, E6, G6
      notes.forEach((freq, i) => {
        const osc = ac.createOscillator();
        const gain = ac.createGain();
        osc.type = i % 2 === 0 ? 'sine' : 'triangle';
        const t = ac.currentTime + i * 0.12;
        osc.frequency.setValueAtTime(freq, t);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.35, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
        osc.connect(gain);
        gain.connect(ac.destination);
        osc.start(t);
        osc.stop(t + 0.48);
      });
    } catch (_) {}
  };

  const segAtPtr = (rot) => {
    const deg = 360 / segments.length;
    // The pointer is drawn at the top (270 degrees or -90 degrees).
    // We need to find the segment i where (rot + i * deg) covers 270.
    const norm = ((270 - (rot % 360)) + 360) % 360;
    return Math.floor(norm / deg) % segments.length;
  };

  const handleSpin = async () => {
    if (spinning || S.current.isSpinning) return;
    S.current.isSpinning = true;
    
    getAudioCtx();
    setWinModal(null);
    setSpinning(true);

    const s = S.current;
    s.isSpinning = true;
    s.spinStartTime = performance.now();
    s.startRotation = s.rotation % 360;
    s.winSeg = null;

    let loadingRafId;
    let loadingRotation = s.startRotation;
    const loadingSpin = (now) => {
        if (!s.isSpinning) return;
        loadingRotation += 15; 
        s.rotation = loadingRotation;
        
        const currentSeg = segAtPtr(s.rotation);
        if (currentSeg !== s.lastSeg) {
          playTick(1);
          s.lastSeg = currentSeg;
        }

        drawWheel();
        loadingRafId = requestAnimationFrame(loadingSpin);
    };
    loadingRafId = requestAnimationFrame(loadingSpin);

    let stopIndex = 0;
    try {
        const { data } = await Axios.post("/spinWheel/spin", { variant: 'jackpot' });
        stopIndex = data?.result?.stopIndex ?? 0;
    } catch (err) {
        console.error(err);
        setSpinning(false);
        s.isSpinning = false;
        cancelAnimationFrame(loadingRafId);
        return;
    }

    cancelAnimationFrame(loadingRafId);

    const num = segments.length;
    const deg = 360 / num;
    const norm = (stopIndex + 0.5) * deg;
    const targetRot = (270 - norm + 360) % 360;

    s.startRotation = s.rotation % 360;
    const delta = (targetRot - s.startRotation + 360) % 360;
    const extraRounds = Math.floor(3 + Math.random() * 2) * 360;
    s.totalRotation = extraRounds + delta;
    
    s.spinStartTime = performance.now();

    let rafId;
    const animate = (now) => {
      const elapsed = now - s.spinStartTime;
      const progress = Math.min(1, elapsed / s.spinDuration);
      
      // Quartic ease-out for authentic mechanical deceleration
      const easeOut = 1 - Math.pow(1 - progress, 4);
      s.rotation = s.startRotation + s.totalRotation * easeOut;

      // Check for segment boundary crossing to trigger click audio
      const currentSeg = segAtPtr(s.rotation);
      if (currentSeg !== s.lastSeg) {
        const velocity = (1 - progress) * 25;
        if (velocity > 0.5) playTick(velocity);
        s.lastSeg = currentSeg;
      }

      drawWheel();

      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        s.isSpinning = false;
        setSpinning(false);
        const winIdx = stopIndex;
        s.winSeg = winIdx;
        drawWheel();

        const wonSeg = segments[winIdx];
        if (wonSeg.pct > 0 || wonSeg.isTicket) {
          playWin();
          const winAmount = wonSeg.pct > 0 ? (jackpotPool * wonSeg.pct) / 100 : 0;
          setTimeout(() => {
            setWinModal({
              title: <><>JACKPOT<br/>HIT!</></>,
              amount: wonSeg.isTicket ? '2X TICKETS' : `$${winAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
              subtitle: 'Congratulations!',
              subtitle2: wonSeg.isTicket ? 'Free jackpot tickets have been added to your account.' : `You claimed ${wonSeg.pct}% of the Jackpot Pool.`,
            });
            if (tickets > 1) {
              setTimeout(() => {
                setWinModal(null);
              }, 2000);
            }
          }, 500);
        }

        if (onSpinResult) onSpinResult(wonSeg);
      }
    };

    rafId = requestAnimationFrame(animate);
  };

  const drawWheel = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width / dpr;
    ctx.clearRect(0, 0, W, W);

    const cx = W / 2;
    const cy = W / 2;
    const outerR = W * 0.44;
    const rimW = W * 0.035;
    const segR = outerR - rimW;
    const num = segments.length;
    const deg = 360 / num;
    const rot = S.current.rotation * RAD;

    ctx.save();
    
    // Outer shadow & dark rim
    ctx.beginPath();
    ctx.arc(cx, cy, outerR + 4, 0, Math.PI * 2);
    ctx.fillStyle = '#1A0C00';
    ctx.fill();

    // Metallic gold rim
    const rimGrad = ctx.createLinearGradient(cx - outerR, cy - outerR, cx + outerR, cy + outerR);
    rimGrad.addColorStop(0.0, '#FFF4A0');
    rimGrad.addColorStop(0.3, '#FFD700');
    rimGrad.addColorStop(0.7, '#DAA520');
    rimGrad.addColorStop(1.0, '#996515');
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.fillStyle = rimGrad;
    ctx.fill();

    // 24 Glowing LED bulbs around rim
    const numBulbs = 24;
    const bulbR = outerR - rimW / 2;
    for (let i = 0; i < numBulbs; i++) {
      const angle = (i * (360 / numBulbs)) * RAD;
      const bx = cx + Math.cos(angle) * bulbR;
      const by = cy + Math.sin(angle) * bulbR;
      ctx.beginPath();
      ctx.arc(bx, by, W * 0.008, 0, Math.PI * 2);
      ctx.fillStyle = (i % 2 === Math.floor(S.current.rotation / 15) % 2) ? '#FFFFFF' : '#FEF08A';
      ctx.shadowColor = '#EAB308';
      ctx.shadowBlur = 6;
      ctx.fill();
    }
    ctx.shadowBlur = 0;

    // Inner rim border
    ctx.beginPath();
    ctx.arc(cx, cy, segR, 0, Math.PI * 2);
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#1A0C00';
    ctx.stroke();

    // Draw segment wedges
    for (let i = 0; i < num; i++) {
      const a0 = rot + i * deg * RAD;
      const a1 = rot + (i + 1) * deg * RAD;
      const mid = (a0 + a1) / 2;
      const seg = segments[i];

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, segR, a0, a1);
      ctx.closePath();
      ctx.fillStyle = jpAltColor(i);
      ctx.fill();

      // Wedge shadow/sheen
      const gx = cx + Math.cos(mid) * segR;
      const gy = cy + Math.sin(mid) * segR;
      const sg = ctx.createLinearGradient(cx, cy, gx, gy);
      sg.addColorStop(0.0, 'rgba(255,255,255,0.25)');
      sg.addColorStop(0.4, 'rgba(255,255,255,0.05)');
      sg.addColorStop(1.0, 'rgba(0,0,0,0.25)');
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, segR, a0, a1);
      ctx.closePath();
      ctx.fillStyle = sg;
      ctx.fill();

      // Border lines between segments
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, segR, a0, a0);
      ctx.strokeStyle = 'rgba(255,215,0,0.6)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Star sparkles in special red segments
      if (seg.stars) {
        ctx.save();
        for (let sIdx = 1; sIdx <= 3; sIdx++) {
          const sAngle = mid + (sIdx - 2) * 0.08;
          const sDist = segR * (0.45 + sIdx * 0.12);
          const sx = cx + Math.cos(sAngle) * sDist;
          const sy = cy + Math.sin(sAngle) * sDist;
          ctx.beginPath();
          ctx.arc(sx, sy, 2, 0, Math.PI * 2);
          ctx.fillStyle = '#FEF08A';
          ctx.shadowColor = '#FACC15';
          ctx.shadowBlur = 4;
          ctx.fill();
        }
        ctx.restore();
      }

      // Draw text
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(mid);
      const fontSize = Math.max(11, W * 0.038);
      ctx.font = `900 ${fontSize}px "Arial Black", Arial, sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = jpAltTextColor(i);
      ctx.shadowColor = 'rgba(0,0,0,0.7)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;

      const lines = seg.label.split('\n');
      const textR = segR * 0.88;
      if (lines.length > 1) {
        ctx.fillText(lines[0], textR, -fontSize * 0.55);
        ctx.fillText(lines[1], textR, fontSize * 0.55);
      } else {
        ctx.fillText(seg.label, textR, 0);
      }
      ctx.restore();
    }

    // Center metallic gold/bronze hub cap
    const hubR = W * 0.13;
    ctx.beginPath();
    ctx.arc(cx, cy, hubR + 4, 0, Math.PI * 2);
    ctx.fillStyle = '#1A0C00';
    ctx.fill();

    const hubGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, hubR);
    hubGrad.addColorStop(0.0, '#FFE599');
    hubGrad.addColorStop(0.3, '#DAA520');
    hubGrad.addColorStop(0.7, '#996515');
    hubGrad.addColorStop(1.0, '#4A2E00');
    ctx.beginPath();
    ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
    ctx.fillStyle = hubGrad;
    ctx.fill();

    // Inner bronze ring
    ctx.beginPath();
    ctx.arc(cx, cy, hubR * 0.65, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.restore();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const size = 360;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    drawWheel();
  }, [segments]);

  const displayPool = Number(jackpotPool || 60025600).toLocaleString('en-US', { minimumFractionDigits: 0 });
  const displayBal = Number(wallet?.balance || 50000).toLocaleString('en-US', { minimumFractionDigits: 0 });
  const displayCred = Number(wallet?.credit || 50000).toLocaleString('en-US', { minimumFractionDigits: 0 });

  return (
    <div className="mjs-container">

      {/* Marquee Sign */}
      <div className="mjs-marquee-wrap">
        <span className="mjs-floating-item" style={{ top: '-12px', left: '10%', fontSize: '20px', animationDelay: '0s' }}>🪙</span>
        <span className="mjs-floating-item" style={{ top: '60%', left: '0%', fontSize: '24px', animationDelay: '0.7s' }}>🪙</span>
        <span className="mjs-floating-item" style={{ top: '-8px', right: '12%', fontSize: '18px', animationDelay: '1.2s' }}>✨</span>
        <span className="mjs-floating-item" style={{ top: '50%', right: '5%', fontSize: '22px', animationDelay: '0.4s' }}>🪙</span>
        <span className="mjs-floating-item" style={{ bottom: '-10px', left: '30%', fontSize: '16px', animationDelay: '1.8s' }}>✨</span>

        <div className="mjs-marquee-frame">
          <div className="mjs-marquee-bulbs">
            {Array.from({ length: 22 }).map((_, i) => {
              // Distribute bulbs around the rectangular border
              let top, left, right, bottom;
              if (i < 7) { top = '-4px'; left = `${(i / 6) * 92 + 4}%`; }
              else if (i < 11) { right = '-4px'; top = `${((i - 6) / 4) * 80 + 10}%`; }
              else if (i < 18) { bottom = '-4px'; left = `${((17 - i) / 6) * 92 + 4}%`; }
              else { left = '-4px'; top = `${((22 - i) / 4) * 80 + 10}%`; }
              return <div key={i} className="mjs-bulb" style={{ top, left, right, bottom, animationDelay: `${(i % 3) * 0.4}s` }} />;
            })}
          </div>
          <div className="mjs-marquee-text">
            JACKPOT<br />SPIN
          </div>
        </div>
      </div>

      {/* Jackpot Pool */}
      <div className="mjs-pool-section">
        <div className="mjs-pool-label">Jackpot Pool:</div>
        <div className="mjs-pool-value">${displayPool}</div>
      </div>

      {/* Wheel Canvas & Pointer */}
      <div className="mjs-wheel-wrap">
        <svg className="mjs-wheel-pointer" viewBox="0 0 40 45" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 45L0 0H40L20 45Z" fill="url(#ptrGrad)" stroke="#7F1D1D" strokeWidth="2"/>
          <defs>
            <linearGradient id="ptrGrad" x1="20" y1="0" x2="20" y2="45" gradientUnits="userSpaceOnUse">
              <stop stopColor="#FACC15" />
              <stop offset="0.5" stopColor="#EAB308" />
              <stop offset="1" stopColor="#B45309" />
            </linearGradient>
          </defs>
        </svg>
        <canvas ref={canvasRef} className="mjs-wheel-canvas" />
      </div>

      {/* Spin Now Button */}
      <button
        type="button"
        onClick={handleSpin}
        disabled={spinning || tickets <= 0}
        className="mjs-spin-btn"
      >
        <span className="mjs-spin-btn-text">Spin Now</span>
        <div className="mjs-spin-btn-badge">
          <span className="mjs-spin-btn-badge-text"> {tickets}</span>
        </div>
      </button>

      {/* Celebrate Win Modal */}
      {winModal && (
        <ResultModal
          open={Boolean(winModal)}
          variant="jackpot"
          title={winModal.title}
          multiplier={winModal.amount}
          subtitle={winModal.subtitle}
          subtitle2={winModal.subtitle2}
          onClose={() => setWinModal(null)}
        />
      )}
    </div>
  );
}


